import {
  Badge,
  Button,
  Divider,
  Drawer,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon
} from '@mantine/core';
import {
  IconCircleCheck,
  IconCreditCard,
  IconEye,
  IconLabel,
  IconPlus,
  IconShirt,
  IconUser,
  IconUsersGroup
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IOrder } from '../../interfaces/Order';
import { IStore } from '../../interfaces/Store';
import { showNotification } from '../../utils/Notification/Notification';
import { buildOrderFolio } from '../../utils/ordersUtils';
import { formatMoneyString, getCustomerFullName } from '../../utils/strings';
import { tryPrintLabel, tryPrintTicket } from '../../utils/ticketHelper';
import DrawerActionCard from '../DrawerActionCard';
import OrderStatuses from '../../enum/OrderStatuses';
import OrderLineAssignmentModal from '../OrderModals/OrderLineAssignmentModal';
import useCanAssignOrderLineEmployees from '../../hooks/useCanAssignOrderLineEmployees';
import { isOrderLineAssignable } from '../../utils/orderLineAssignments';
import ServiceLabelSelector from '../ServiceLabelSelector';
import useCanEditOrders from '../../hooks/useCanEditOrders';

interface OrderCreatedDrawerProps {
  opened: boolean;
  onClose: () => void;
  order: IOrder | null;
  store: IStore | null;
  onStartNewOrder: () => void;
  onViewOrder: (orderId: string) => void;
  onCollectPayment?: (orderId: string) => void;
  onOrderUpdated?: (updatedOrder: IOrder) => void;
}

const getPaymentSummary = (order: IOrder | null) => {
  const total = order?.total || 0;
  const paymentLinesTotal = Array.isArray(order?.paymentLines)
    ? order?.paymentLines.reduce((acc, line) => acc + (line?.amountPaid || 0), 0)
    : 0;
  const amountPaid =
    typeof order?.amountPaid === 'number' && !Number.isNaN(order?.amountPaid)
      ? order?.amountPaid
      : paymentLinesTotal;
  const amountDue = Math.max(0, total - (amountPaid || 0));
  const isPaid = amountDue <= 0 && total > 0;
  return { total, amountPaid, amountDue, isPaid };
};

const OrderCreatedSummaryCard = ({
  order,
  store
}: {
  order: IOrder | null;
  store: IStore | null;
}) => {
  const { t } = useTranslation(['NewOrder', 'CommonButtons', 'FieldName']);
  const { total, amountDue, isPaid } = getPaymentSummary(order);
  const folio =
    order?.sequence || order?.sequence === 0
      ? buildOrderFolio(order?.folioPrefix || store?.orderFolioPrefix, order?.sequence)
      : t('CommonButtons:noData');
  const customerName =
    order?.customer && typeof order?.customer !== 'string'
      ? getCustomerFullName(order?.customer?.name, order?.customer?.lastName)
      : t('CommonButtons:noData');
  const readyDate = order?.readyDateTime
    ? moment(order.readyDateTime).format('DD/MM/YYYY hh:mm A')
    : null;
  const totalQuantity =
    typeof order?.totalQuantity === 'number' && order?.totalQuantity > 0
      ? order?.totalQuantity
      : null;
  const orderStatus = t(
    `FieldName:orderStatuses.${OrderStatuses[order?.status?.toUpperCase() || '']}`
  );

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      data-testid="order-created-summary-card"
      data-order-id={order?._id || ''}>
      <Group justify="space-between" align="flex-start" gap="sm">
        <Group align="center" gap="sm">
          <ThemeIcon color="green" variant="light" radius="xl">
            <IconCircleCheck size={18} />
          </ThemeIcon>
          <Stack gap={0}>
            <Text fw={600}>{t('NewOrder:orderCreated')}</Text>
            <Text size="sm" c="dimmed">
              {t('NewOrder:orderSummary')}
            </Text>
          </Stack>
        </Group>
        <Badge color={isPaid ? 'green' : 'yellow'} variant="light">
          {isPaid ? t('NewOrder:paymentPaid') : t('NewOrder:paymentPending')}
        </Badge>
      </Group>
      <Divider my="sm" />
      <Stack gap={6}>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {t('FieldName:folio')}
          </Text>
          <Text fw={600} data-testid="order-created-folio-value">
            {folio}
          </Text>
        </Group>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {t('FieldName:customer')}
          </Text>
          <Text>{customerName}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {t('FieldName:total')}
          </Text>
          <Text fw={600} data-testid="order-created-total-value">
            {formatMoneyString(total.toFixed(2))}
          </Text>
        </Group>
        {amountDue > 0 && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {t('FieldName:amountDue')}
            </Text>
            <Text fw={600}>{formatMoneyString(amountDue.toFixed(2))}</Text>
          </Group>
        )}
        {readyDate && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {t('NewOrder:promiseDate')}
            </Text>
            <Text>{readyDate}</Text>
          </Group>
        )}
        {totalQuantity && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {t('FieldName:quantity')}
            </Text>
            <Text>{totalQuantity}</Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );
};

const OrderCreatedDrawer = ({
  opened,
  onClose,
  order,
  store,
  onStartNewOrder,
  onViewOrder,
  onCollectPayment,
  onOrderUpdated
}: OrderCreatedDrawerProps) => {
  const labelIcon = <IconLabel />;
  const { t } = useTranslation([
    'NewOrder',
    'CommonButtons',
    'FieldName',
    'OrdersCardMoreActionMenu',
    'Labels'
  ]);
  const { company } = useSelector((state: any) => state.user);
  const canEditOrders = useCanEditOrders();
  const canAssignEmployees = useCanAssignOrderLineEmployees();
  const [serviceLabelsOpened, serviceLabels] = useDisclosure(false);
  const [openedAssignmentsDrawer, assignmentsDrawerHandlers] = useDisclosure(false);
  const isDisabled = !order || !store;
  const canAssignServiceManagers = canAssignEmployees && (!order || [
    ...(order.products || []), ...(order.buyAndGetProducts || [])
  ].some(isOrderLineAssignable));
  const { amountDue, isPaid } = getPaymentSummary(order);
  const shouldShowCollectPayment = Boolean(onCollectPayment) && amountDue > 0 && !isPaid;
  const showAutoPrintedHint = Boolean(
    store?.orderPageConfig?.printTicketAfterOrder || store?.printingConfig?.printTicketForLaundry
  );

  const onPrintTicket = async (
    ticketStructureConfig: 'ticketForLaundryStructure' | 'ticketStructure'
  ) => {
    if (!order || !store) return;
    try {
      tryPrintTicket(
        order,
        store,
        t,
        ticketStructureConfig,
        company?.logoUrl || '',
        false,
        company?.companyTaxInfo
      );
      showNotification({
        type: 'Default',
        title: t('NewOrder:printQueued')
      });
    } catch (e) {}
  };

  const onPrintLabelTicket = async () => {
    if (!order || !store) return;
    try {
      await tryPrintLabel(order, store, t, [], company?.logoUrl || '');
    } catch (e) {}
  };

  return (
    <>
      <Drawer
        data-testid="order-created-drawer"
        opened={opened}
        onClose={onClose}
        position="right"
        title={t('NewOrder:orderCreated')}
        padding="md"
        size="sm">
        <Stack gap="md">
          <OrderCreatedSummaryCard order={order} store={store} />
          <Stack gap="xs">
            <Button
              data-testid="order-created-start-new-order"
              fullWidth
              leftSection={<IconPlus size={18} />}
              onClick={() => {
                onClose();
                onStartNewOrder();
              }}
              disabled={isDisabled}>
              {t('NewOrder:startNewOrder')}
            </Button>
            <Button
              data-testid="order-created-edit-order"
              fullWidth
              variant="default"
              leftSection={<IconEye size={18} />}
              onClick={() => {
                if (!order?._id) return;
                onClose();
                onViewOrder(order._id);
              }}
              disabled={isDisabled || !canEditOrders}>
              {t('NewOrder:editOrder')}
            </Button>
            {shouldShowCollectPayment && (
              <Button
                data-testid="order-created-collect-payment"
                fullWidth
                variant="light"
                color="orange"
                leftSection={<IconCreditCard size={18} />}
                onClick={() => {
                  if (!order?._id || !onCollectPayment) return;
                  onCollectPayment(order._id);
                }}
                disabled={isDisabled}>
                {t('NewOrder:collectPayment')}
              </Button>
            )}
            {canAssignServiceManagers && (
              <Button
                data-testid="order-created-assign-service-managers"
                fullWidth
                variant="light"
                leftSection={<IconUsersGroup size={18} />}
                onClick={assignmentsDrawerHandlers.open}
                disabled={isDisabled}>
                {t('NewOrder:assignServiceManagers')}
              </Button>
            )}
          </Stack>
          <Divider />
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text fw={600}>{t('NewOrder:reprintSectionTitle')}</Text>
              {showAutoPrintedHint && (
                <Text size="xs" c="dimmed">
                  {t('NewOrder:autoPrintedHint')}
                </Text>
              )}
            </Group>
            <DrawerActionCard
              label={t('OrdersCardMoreActionMenu:printTicketSheetOptions.customerTicket')}
              icon={<IconUser />}
              iconColor="blue"
              onClick={() => onPrintTicket('ticketStructure')}
              disabled={isDisabled}
              ariaLabel={t('OrdersCardMoreActionMenu:printTicketSheetOptions.customerTicket')}
            />
            <DrawerActionCard
              label={t('OrdersCardMoreActionMenu:printTicketSheetOptions.laundryTicket')}
              icon={<IconShirt />}
              iconColor="teal"
              onClick={() => onPrintTicket('ticketForLaundryStructure')}
              disabled={isDisabled}
              ariaLabel={t('OrdersCardMoreActionMenu:printTicketSheetOptions.laundryTicket')}
            />
            <DrawerActionCard
              label={t('Labels:orderMode')}
              icon={labelIcon}
              iconColor="lime"
              onClick={onPrintLabelTicket}
              disabled={isDisabled}
              ariaLabel={t('Labels:orderMode')}
            />
            <DrawerActionCard
              label={t('Labels:serviceMode')}
              icon={labelIcon}
              iconColor="lime"
              onClick={serviceLabels.open}
              disabled={isDisabled}
              ariaLabel={t('Labels:serviceMode')}
            />
          </Stack>
        </Stack>
      </Drawer>
      {store && <ServiceLabelSelector opened={opened && serviceLabelsOpened} onClose={serviceLabels.close} orderId={order?._id || ''} store={store} logo={company?.logoUrl || ''} />}
      {canAssignServiceManagers ? (
        <OrderLineAssignmentModal
          opened={openedAssignmentsDrawer}
          close={assignmentsDrawerHandlers.close}
          order={order}
          orderId={order?._id || ''}
          onSaved={(updatedOrder) => {
            onOrderUpdated?.(updatedOrder);
          }}
        />
      ) : null}
    </>
  );
};

export default OrderCreatedDrawer;
